import type { CSSProperties } from 'react';
import { MOTION } from '../theme/tokens.js';

/**
 * The motion budget, applied.
 *
 * Every duration and easing here comes from `MOTION` in the tokens. Nothing is
 * hardcoded, so changing the budget is a one-file change and a reviewer can
 * check compliance by reading the imports.
 *
 * The one exception is the easing for the 150ms default, which the handoff
 * does not name and `MOTION` does not carry. It is declared once, below, and
 * should move into the tokens if a designer ever fixes it.
 */

/**
 * Easing for everything that is not a sheet. BUILD_PLAN 9.5 says "150ms
 * opacity and 4px translate" without naming a curve, and names ease-out for
 * the only other timed value it details (the 120ms score roll), so ease-out is
 * the consistent read. Not a token; see the note above.
 */
export const DEFAULT_EASING = 'ease-out';

/** How a surface is being presented. Below 640px a Dialog is a Sheet. */
export type SurfacePresentation = 'dialog' | 'sheet';

/** Which keyframe pair a surface animates with. `fade` is the reduced set. */
export type MotionName = 'dialog' | 'sheet' | 'fade';

export interface MotionSpec {
  readonly name: MotionName;
  readonly durationMs: number;
  readonly easing: string;
  /** False when transforms are suppressed and only opacity animates. */
  readonly transforms: boolean;
}

/**
 * The motion for a dialog or sheet surface.
 *
 * `prefers-reduced-motion` is checked FIRST and collapses both presentations to
 * an opacity fade at the default duration. The handoff's rule is "disable
 * transforms, keep opacity only", and the 240ms spring exists only to sell the
 * translate: with no translate to sell, a sheet gets the same quiet 150ms fade
 * as everything else.
 */
export function surfaceMotion(
  presentation: SurfacePresentation,
  reducedMotion: boolean,
): MotionSpec {
  if (reducedMotion) {
    return { name: 'fade', durationMs: MOTION.default, easing: DEFAULT_EASING, transforms: false };
  }
  if (presentation === 'sheet') {
    return {
      name: 'sheet',
      durationMs: MOTION.sheet,
      easing: MOTION.sheetEasing,
      transforms: true,
    };
  }
  return { name: 'dialog', durationMs: MOTION.default, easing: DEFAULT_EASING, transforms: true };
}

/**
 * Motion for anything that only ever fades: the scrim, and a toast.
 *
 * Reduced motion changes nothing here, which is the point of returning it from
 * a function that takes the flag: the caller does not have to know that.
 */
export function fadeMotion(reducedMotion: boolean): MotionSpec {
  return {
    name: reducedMotion ? 'fade' : 'dialog',
    durationMs: MOTION.default,
    easing: DEFAULT_EASING,
    transforms: !reducedMotion,
  };
}

/** A style object that may carry CSS custom properties. */
export type StyleWithVars = CSSProperties & Record<`--${string}`, string>;

/** Hand a spec to CSS. The stylesheet reads these two custom properties. */
export function motionStyle(spec: MotionSpec): StyleWithVars {
  return {
    '--gs-motion-duration': `${String(spec.durationMs)}ms`,
    '--gs-motion-easing': spec.easing,
  };
}
